import { EyeOutlined } from '@ant-design/icons';
import { RiskActionButton } from '@wanjianshi/admin-ui';
import type { ApiResponse } from '@wanjianshi/shared-contracts';
import { Alert, Button, Modal, Space, Typography, message } from '@wanjianshi/admin-ui';
import { EntityFormModal } from '@/components/EntityFormModal';
import { MembershipEditorModal } from '@/features/memberships/MembershipEditorModal';
import { RechargePointReversalActions } from '@/features/recharge/RechargePointReversalActions';
import { PermissionButton } from '@/components/PermissionButton';
import { AdminRoleAssignmentModal } from '@/features/admin-users/AdminRoleAssignmentModal';
import { InviterBindingModal } from '@/features/users/InviterBindingModal';
import { useAdminAuth } from '@/auth/store';
import { useNavigate } from 'react-router-dom';
import { adminApi, adminOperationOptions, createIdempotencyKey } from '@/services/api/client';
import { endpointBase, type ResourceDefinition } from './definitions';

type Row = Record<string, unknown> & { id?: string | number };

async function mutate(
  method: 'post' | 'put' | 'delete',
  path: string,
  body: unknown,
  reason?: string,
  idempotencyKey?: string,
) {
  const options = adminOperationOptions(reason, idempotencyKey);
  if (method === 'post') return adminApi.post<ApiResponse<unknown>>(path, body, options);
  if (method === 'put') return adminApi.put<ApiResponse<unknown>>(path, body, options);
  return adminApi.delete<ApiResponse<unknown>>(path, options);
}

export function ResourceRowActions({
  endpoint,
  row,
  definition,
  onReload,
  onDetail,
}: {
  endpoint: string;
  row: Row;
  definition?: ResourceDefinition;
  onReload: () => Promise<void>;
  onDetail: (row: Row) => void;
}) {
  const base = endpointBase(endpoint);
  const navigate = useNavigate();
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const [modal, contextHolder] = Modal.useModal();
  const id = row.id;
  const completed = new Set(['succeeded', 'completed', 'complete', 'failed', 'cancelled', 'canceled', 'partial', 'expired']);
  const action = async (method: 'post' | 'put' | 'delete', path: string, body: unknown, reason?: string, idempotencyKey?: string) => {
    await mutate(method, path, body, reason, idempotencyKey);
    message.success('操作成功');
    await onReload();
  };
  const edit = definition?.edit && hasPermission(definition.edit.permission) ? (
    <EntityFormModal
      title={definition.edit.title}
      buttonLabel={definition.edit.buttonLabel}
      fields={definition.edit.fields}
      initialValues={row}
      reasonRequired={definition.edit.reasonRequired}
      buttonAction="edit"
      buttonProps={{ size: 'small' }}
      onSubmit={async (values, reason) => {
        const path = typeof definition.edit!.path === 'function' ? definition.edit!.path(row) : definition.edit!.path;
        await action(definition.edit!.method, path, values, reason);
      }}
    />
  ) : null;

  const commonDelete = (
    permission: string,
    path: string,
    title: string,
  ) => (
    <RiskActionButton
      size="small"
      allowed={hasPermission(permission)}
      title={title}
      description="删除后该资源不再参与业务查询，请确认关联影响。"
      onConfirm={reason => action('delete', path, undefined, reason)}
    >删除</RiskActionButton>
  );

  return (
    <Space size={4} wrap>
      {contextHolder}
      <Button size="small" icon={<EyeOutlined />} onClick={() => {
        if (base === '/admin/teams') navigate(`/teams/${id}`);
        else if (base === '/admin/team-memberships' && hasPermission('wjs-admin:team:detail')) navigate(`/teams/${row.subject_id}`);
        else if (base === '/admin/projects') navigate(`/projects/${id}`);
        else onDetail(row);
      }}>详情</Button>
      {edit}
      {base === '/admin/personal-memberships' ? (
        <MembershipEditorModal kind="personal" mode="edit" row={row} onSaved={onReload} />
      ) : null}
      {base === '/admin/team-memberships' ? (
        <MembershipEditorModal kind="team" mode="edit" row={row} onSaved={onReload} />
      ) : null}

      {base === '/admin/users' ? (
        <>
          <InviterBindingModal
            userId={String(id)}
            userLabel={String(row.nickname || row.username || `用户 ${String(id)}`)}
            inviteeCount={Number(row.invitee_count || 0)}
            onSaved={onReload}
          />
          {Number(row.status) !== 0 ? (
            <RiskActionButton size="small" danger={false} allowed={hasPermission('wjs-admin:user:enable')} title="启用用户" onConfirm={reason => action('post', `/admin/users/${id}/enable`, undefined, reason)}>启用</RiskActionButton>
          ) : null}
          <EntityFormModal
            title={`重置密码 · ${String(row.nickname || `用户 ${id}`)}`}
            description={(
              <Alert
                type="warning"
                showIcon
                title={`当前操作对象：${String(row.nickname || `用户 ${id}`)}（用户 ID：${String(id)}）`}
                description={`只会重置当前选中用户${row.mobile_masked ? `（${String(row.mobile_masked)}）` : ''}的密码，不会修改当前登录管理员的密码。重置成功后，该用户现有登录会话将退出。`}
              />
            )}
            buttonLabel="重置密码"
            fields={[{
              name: 'password',
              label: '新密码',
              type: 'password',
              required: true,
              minLength: 8,
              maxLength: 128,
              placeholder: '请输入 8–128 位新密码',
            }]}
            reasonRequired
            buttonProps={{ size: 'small', danger: true, style: { display: hasPermission('wjs-admin:user:reset-password') ? undefined : 'none' } }}
            onSubmit={async (values, reason) => action('post', `/admin/users/${id}/reset-password`, values, reason)}
          />
        </>
      ) : null}

      {base === '/admin/admin-users' ? (
        <>
          <AdminRoleAssignmentModal
            userId={String(id)}
            userLabel={String(row.nickname || row.username || `用户 ${String(id)}`)}
            initialRoleIds={row.role_ids}
            onSaved={onReload}
          />
          <RiskActionButton size="small" allowed={hasPermission('wjs-admin:admin-user:force-logout')} title="强制退出后台" description="只吊销后台 Token，用户端登录不受影响。" onConfirm={reason => action('post', `/admin/admin-users/${id}/force-logout`, undefined, reason)}>退出后台</RiskActionButton>
        </>
      ) : null}

      {base === '/admin/roles' ? (
        <>
          <EntityFormModal
            title="配置角色菜单与操作权限"
            buttonLabel="分配菜单"
            fields={[{ name: 'menu_ids', label: '菜单/权限 ID 数组', type: 'json', required: true, placeholder: '[101, 102]' }]}
            reasonRequired
            buttonProps={{ size: 'small', style: { display: hasPermission('wjs-admin:role:assign-menu') ? undefined : 'none' } }}
            onSubmit={async (values, reason) => action('put', `/admin/roles/${id}/menus`, values, reason)}
          />
          <EntityFormModal
            title="配置用户数据范围"
            buttonLabel="数据范围"
            fields={[
              { name: 'data_scope', label: '数据范围', type: 'select', required: true, options: [
                { label: '全部数据', value: 1 }, { label: '自定义部门', value: 2 }, { label: '本部门', value: 3 },
                { label: '本部门及下级', value: 4 }, { label: '仅本人', value: 5 },
              ] },
              { name: 'dept_ids', label: '自定义部门 ID 数组', type: 'json', placeholder: '[10, 11]' },
            ]}
            initialValues={{ data_scope: row.data_scope, dept_ids: row.data_scope_dept_ids }}
            reasonRequired
            buttonProps={{ size: 'small', style: { display: hasPermission('wjs-admin:role:data-scope') ? undefined : 'none' } }}
            onSubmit={async (values, reason) => action('put', `/admin/roles/${id}/data-scope`, values, reason)}
          />
          {commonDelete('wjs-admin:role:delete', `/admin/roles/${id}`, '删除后台角色')}
        </>
      ) : null}

      {base === '/admin/menus' ? commonDelete('wjs-admin:menu:delete', `/admin/menus/${id}`, '删除菜单或权限') : null}
      {base === '/admin/departments' ? commonDelete('wjs-admin:department:delete', `/admin/departments/${id}`, '删除部门') : null}
      {base === '/admin/posts' ? commonDelete('wjs-admin:post:delete', `/admin/posts/${id}`, '删除岗位') : null}
      {base === '/admin/sessions' ? <RiskActionButton size="small" allowed={hasPermission('wjs-admin:session:revoke')} title="吊销后台会话" onConfirm={reason => action('post', `/admin/sessions/${id}/revoke`, undefined, reason)}>吊销</RiskActionButton> : null}

      {base === '/admin/membership-plans' ? (
        Number(row.status) === 1
          ? <RiskActionButton size="small" allowed={hasPermission('wjs-admin:membership-plan:disable')} title="停用套餐" onConfirm={reason => action('post', `/admin/membership-plans/${id}/disable`, undefined, reason)}>停用</RiskActionButton>
          : <RiskActionButton size="small" danger={false} allowed={hasPermission('wjs-admin:membership-plan:enable')} title="启用套餐" onConfirm={reason => action('post', `/admin/membership-plans/${id}/enable`, undefined, reason)}>启用</RiskActionButton>
      ) : null}

      {base === '/admin/generation-tasks' ? (
        <>
          {!completed.has(String(row.status).toLowerCase()) ? <RiskActionButton size="small" allowed={hasPermission('wjs-admin:generation-task:cancel')} title="取消生成任务" description="已成功的子项会保留，未完成子项将取消并按账单状态退款。" onConfirm={reason => action('post', `/admin/generation-tasks/${row.task_id}/cancel`, undefined, reason)}>取消</RiskActionButton> : null}
          {['failed', 'cancelled', 'canceled', 'partial', 'expired'].includes(String(row.status).toLowerCase()) ? <RiskActionButton size="small" danger={false} allowed={hasPermission('wjs-admin:generation-task:retry')} title="安全重试生成任务" description="会生成新任务；Suno 只恢复已有供应商任务轮询。" onConfirm={reason => action('post', `/admin/generation-tasks/${row.task_id}/retry`, undefined, reason, createIdempotencyKey('task-retry'))}>重试</RiskActionButton> : null}
        </>
      ) : null}

      {base === '/admin/banners' ? commonDelete('wjs-admin:banner:delete', `/admin/banners/${id}`, '删除 Banner') : null}
      {base === '/admin/redeem-codes' && Number(row.status) === 1 ? <RiskActionButton size="small" allowed={hasPermission('wjs-admin:redeem-code:disable')} title="停用兑换码" onConfirm={reason => action('post', `/admin/redeem-codes/${id}/disable`, undefined, reason)}>停用</RiskActionButton> : null}
      {base === '/admin/creator-certifications/users' ? (
        <>
          <EntityFormModal title="通过创作者认证" buttonLabel="通过" fields={[
            { name: 'badge_type_id', label: '认证类型 ID', type: 'number', required: true },
            { name: 'is_primary', label: '设为主认证', type: 'switch' }, { name: 'display_order', label: '展示顺序', type: 'number' },
          ]} reasonRequired buttonProps={{ size: 'small', type: 'primary', style: { display: hasPermission('wjs-admin:creator-certification:approve') ? undefined : 'none' } }}
          onSubmit={async (values, reason) => action('post', `/admin/creator-certifications/users/${row.user_id ?? id}/approve`, { ...values, reason }, reason)} />
          <EntityFormModal title="撤销或拒绝创作者认证" buttonLabel="拒绝/撤销" fields={[{ name: 'badge_type_id', label: '认证类型 ID', type: 'number', required: true }]} reasonRequired buttonProps={{ size: 'small', danger: true, style: { display: hasPermission('wjs-admin:creator-certification:reject') ? undefined : 'none' } }}
          onSubmit={async (values, reason) => action('post', `/admin/creator-certifications/users/${row.user_id ?? id}/reject`, { ...values, reason }, reason)} />
        </>
      ) : null}

      {base === '/admin/city-agents' ? (
        Number(row.status) === 0
          ? <PermissionButton size="small" permission="wjs-admin:city-agent:disable" onClick={() => void action('put', `/admin/city-agents/${id}/status`, { status: 1 })}>停用</PermissionButton>
          : <PermissionButton size="small" permission="wjs-admin:city-agent:enable" onClick={() => void action('put', `/admin/city-agents/${id}/status`, { status: 0 })}>启用</PermissionButton>
      ) : null}

      {base === '/admin/platforms' ? (
        <>
          <PermissionButton size="small" permission={Number(row.status) === 1 ? 'wjs-admin:platform:disable' : 'wjs-admin:platform:enable'} onClick={() => void action('put', `/admin/platforms/${id}/status`, { status: Number(row.status) === 1 ? 0 : 1 })}>{Number(row.status) === 1 ? '停用' : '启用'}</PermissionButton>
          {commonDelete('wjs-admin:platform:delete', `/admin/platforms/${id}`, '删除 AI 平台')}
        </>
      ) : null}

      {base === '/admin/api-keys' ? (
        <>
          <PermissionButton size="small" permission={Number(row.status) === 1 ? 'wjs-admin:api-key:disable' : 'wjs-admin:api-key:enable'} onClick={() => void action('put', `/admin/api-keys/${id}/status`, { status: Number(row.status) === 1 ? 0 : 1 })}>{Number(row.status) === 1 ? '停用' : '启用'}</PermissionButton>
          <RiskActionButton size="small" allowed={hasPermission('wjs-admin:api-key:reveal')} title="查看 API Key 明文" description="明文仅在本次弹窗显示并会记录审计日志。" onConfirm={async reason => {
            const response = await adminApi.get<ApiResponse<{ api_key: string }>>(`/admin/api-keys/${id}/secret`, adminOperationOptions(reason));
            modal.info({ title: 'API Key 明文', width: 640, content: <Typography.Text copyable code>{response.data.api_key}</Typography.Text> });
          }}>查看明文</RiskActionButton>
          <EntityFormModal title="轮换 API Key" buttonLabel="轮换" fields={[{ name: 'api_key', label: '新 API Key', type: 'password', required: true }]} reasonRequired buttonProps={{ size: 'small', danger: true, style: { display: hasPermission('wjs-admin:api-key:rotate') ? undefined : 'none' } }} onSubmit={async (values, reason) => action('post', `/admin/api-keys/${id}/rotate`, values, reason, createIdempotencyKey('key-rotate'))} />
          {commonDelete('wjs-admin:api-key:delete', `/admin/api-keys/${id}`, '删除 API Key')}
        </>
      ) : null}

      {base === '/admin/models' ? (
        <>
          <PermissionButton size="small" permission={Number(row.status) === 1 ? 'wjs-admin:model:disable' : 'wjs-admin:model:enable'} onClick={() => void action('put', `/admin/models/${id}/status`, { status: Number(row.status) === 1 ? 0 : 1 })}>{Number(row.status) === 1 ? '停用' : '启用'}</PermissionButton>
          {commonDelete('wjs-admin:model:delete', `/admin/models/${id}`, '删除模型')}
        </>
      ) : null}

      {base === '/admin/suppliers' ? (
        <>
          <PermissionButton size="small" permission={Boolean(row.enabled) ? 'wjs-admin:supplier:disable' : 'wjs-admin:supplier:enable'} onClick={() => void action('put', `/admin/suppliers/${id}/status`, { enabled: !Boolean(row.enabled) })}>{Boolean(row.enabled) ? '停用' : '启用'}</PermissionButton>
          {commonDelete('wjs-admin:supplier:delete', `/admin/suppliers/${id}`, '删除供应商')}
        </>
      ) : null}

      {base === '/admin/routing' ? (
        <>
          <PermissionButton size="small" permission={String(row.status) === 'active' ? 'wjs-admin:routing:disable' : 'wjs-admin:routing:enable'} onClick={() => void action('put', `/admin/routing/${id}/status`, { status: String(row.status) === 'active' ? 'inactive' : 'active' })}>{String(row.status) === 'active' ? '停用' : '启用'}</PermissionButton>
          <PermissionButton size="small" permission="wjs-admin:routing:execute" onClick={async () => { const response = await adminApi.post<ApiResponse<unknown>>(`/admin/routing/${id}/test`); modal.info({ title: '路由配置测试', width: 720, content: <pre>{JSON.stringify(response.data, null, 2)}</pre> }); }}>测试</PermissionButton>
          {commonDelete('wjs-admin:routing:delete', `/admin/routing/${id}`, '删除路由规则')}
        </>
      ) : null}

      {base === '/admin/pricing-rules' ? (
        <>
          <PermissionButton size="small" permission={Boolean(row.is_active) ? 'wjs-admin:pricing-rule:disable' : 'wjs-admin:pricing-rule:enable'} onClick={() => void action('put', `/admin/pricing-rules/${id}/status`, { enabled: !Boolean(row.is_active) })}>{Boolean(row.is_active) ? '停用' : '启用'}</PermissionButton>
          {commonDelete('wjs-admin:pricing-rule:delete', `/admin/pricing-rules/${id}`, '删除计价规则')}
        </>
      ) : null}

      {base === '/admin/recharge' ? (
        <RechargePointReversalActions row={row} onReload={onReload} />
      ) : null}

    </Space>
  );
}
